import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { SnagStatusBadge, SnagPriorityBadge } from '../../snags-list-creation/components/SnagStatusBadge';

const SnagsTab = ({ siteId, snags, onRefresh, isReadOnly = false }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const canCreate = !isReadOnly && ['admin', 'site_manager', 'worker']?.includes(userProfile?.role);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">{t('Snags')}</h3>
        {canCreate && (
          <Button
            size="sm"
            iconName="Plus"
            onClick={() => navigate(`/snags-list-creation?siteId=${siteId}`)}
          >
            {t('reportSnag')}
          </Button>
        )}
      </div>

      {/* Summary stats */}
      {snags?.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: t('open'), count: snags?.filter(s => s?.status === 'open')?.length, color: 'text-red-600', bg: 'bg-red-50' },
            { label: t('inProgress'), count: snags?.filter(s => s?.status === 'in_progress')?.length, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: t('resolved'), count: snags?.filter(s => s?.status === 'resolved')?.length, color: 'text-green-600', bg: 'bg-green-50' }
          ]?.map(stat => (
            <div key={stat?.label} className={`${stat?.bg} rounded-lg p-3 text-center`}>
              <p className={`text-xl font-bold ${stat?.color}`}>{stat?.count}</p>
              <p className="text-xs text-muted-foreground">{stat?.label}</p>
            </div>
          ))}
        </div>
      )}

      {snags?.length > 0 ? (
        <div className="space-y-3">
          {snags?.map(snag => (
            <div
              key={snag?.id}
              className="bg-muted/50 rounded-lg border border-border p-4 hover:bg-muted/70 transition-colors cursor-pointer"
              onClick={() => navigate(`/snags/${snag?.id}`)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="font-medium text-foreground text-sm">{snag?.title}</h4>
                    <SnagStatusBadge status={snag?.status} />
                    <SnagPriorityBadge priority={snag?.priority} />
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{snag?.description}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {snag?.location && (
                      <span className="flex items-center gap-1">
                        <Icon name="MapPin" size={10} />
                        {snag?.location}
                      </span>
                    )}
                    <span>{new Date(snag?.created_at)?.toLocaleDateString()}</span>
                    {snag?.photo_paths?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Icon name="Image" size={10} />
                        {snag?.photo_paths?.length}
                      </span>
                    )}
                  </div>
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground flex-shrink-0 mt-1" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-muted/50 rounded-lg p-12 border border-border text-center">
          <Icon name="ClipboardCheck" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-foreground mb-2">{t('noSnagsRecorded')}</h4>
          <p className="text-sm text-muted-foreground mb-4">{t('noSnagsDesc')}</p>
          {canCreate && (
            <Button
              size="sm"
              iconName="Plus"
              onClick={() => navigate(`/snags-list-creation?siteId=${siteId}`)}
            >
              {t('reportSnag')}
            </Button>
          )}
        </div>
      )}

      {snags?.length > 0 && (
        <div className="text-center pt-2">
          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            onClick={() => navigate(`/snags-list-creation?siteId=${siteId}`)}
          >
            {t('viewAllSnags')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SnagsTab;
